import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Tooltip,
  Divider,
  FormControlLabel,
  Switch,
  Chip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DescriptionIcon from '@mui/icons-material/Description';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CodeIcon from '@mui/icons-material/Code';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import BusinessIcon from '@mui/icons-material/Business';
import CloseIcon from '@mui/icons-material/Close';
import { saveMarkdown, downloadDocument } from '../utils/api';
import { downloadDocumentById, createDocument } from '../utils/documentService';
import { enhanceTechnicalSectionWithAI } from '../utils/api';
import DocumentTags from '../components/DocumentTags';
import DocumentProjectAccess from '../components/DocumentProjectAccess';
import { useAuth } from '../contexts/AuthContext';

const ViewTechnicalSpecPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  // Document state
  const [document, setDocument] = useState('');
  const [originalAIContent, setOriginalAIContent] = useState('');
  const [documentSections, setDocumentSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('preview');
  
  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [sectionEditDialogOpen, setSectionEditDialogOpen] = useState(false);
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  
  // Section editing
  const [currentSection, setCurrentSection] = useState(null);
  const [currentSectionContent, setCurrentSectionContent] = useState('');
  
  // AI enhancement
  const [aiMenuAnchor, setAIMenuAnchor] = useState(null);
  const [aiEnhancementLoading, setAIEnhancementLoading] = useState(false);
  const [aiSuggestions, setAISuggestions] = useState([]);
  const [showAISuggestions, setShowAISuggestions] = useState(false);
  
  // Document saving
  const [savingDocument, setSavingDocument] = useState(false);
  const [documentSaved, setDocumentSaved] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({});
  
  // Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  // Document management
  const [savedDocumentId, setSavedDocumentId] = useState(null);
  const [documentName, setDocumentName] = useState('');
  const [tags, setTags] = useState([]);

  useEffect(() => {
    loadDocumentFromSession();
  }, []);

  const loadDocumentFromSession = () => {
    try {
      const generatedDoc = sessionStorage.getItem('generatedDocument');
      const originalContent = sessionStorage.getItem('originalAIContent');
      const storedFormData = sessionStorage.getItem('formData');
      const docSaved = sessionStorage.getItem('documentSaved') === 'true';
      const savedId = sessionStorage.getItem('saved_document_id');
      
      if (generatedDoc) {
        setDocument(generatedDoc);
        setOriginalAIContent(originalContent || generatedDoc);
        setDocumentSaved(docSaved);
        setSavedDocumentId(savedId);
        
        if (storedFormData) {
          const parsedFormData = JSON.parse(storedFormData);
          setFormData(parsedFormData);
          setDocumentName(parsedFormData.project_name 
            ? `${parsedFormData.project_name} - SAP Technical Specification` 
            : "SAP Technical Specification"
          );
        }
        
        // Parse document into sections
        parseDocumentSections(generatedDoc);
      } else {
        setError('No technical specification found. Please generate one first.');
      }
    } catch (err) {
      console.error('Error loading document from session:', err);
      setError('Error loading document. Please try generating again.');
    } finally {
      setLoading(false);
    }
  };

  const parseDocumentSections = (content) => {
    const lines = content.split('\n');
    const sections = [];
    let currentSection = null;
    let currentContent = [];

    lines.forEach(line => {
      if (line.startsWith('# ') && !line.includes('Document Information') && !line.includes('Document Revision')) {
        if (currentSection) {
          sections.push({
            title: currentSection,
            content: currentContent.join('\n').trim()
          });
        }
        currentSection = line.substring(2).trim();
        currentContent = [];
      } else if (currentSection) {
        currentContent.push(line);
      }
    });

    if (currentSection) {
      sections.push({
        title: currentSection,
        content: currentContent.join('\n').trim()
      });
    }

    setDocumentSections(sections);
  };

  const handleEditSection = (section) => {
    setCurrentSection(section);
    setCurrentSectionContent(section.content);
    setSectionEditDialogOpen(true);
  };

  const handleSaveSection = () => {
    const updatedDocument = document.replace(
      new RegExp(`# ${currentSection.title}[\\s\\S]*?(?=# |$)`),
      `# ${currentSection.title}\n${currentSectionContent}\n\n`
    );
    
    setDocument(updatedDocument);
    sessionStorage.setItem('generatedDocument', updatedDocument);
    parseDocumentSections(updatedDocument);
    setSectionEditDialogOpen(false);
    setSnackbarMessage('Section updated successfully');
    setSnackbarOpen(true);
  };

  const handleAIEnhancement = async (enhancementType) => {
    if (!currentSection || !currentUser) {
      setSnackbarMessage('Please select a section and ensure you are logged in');
      setSnackbarOpen(true);
      return;
    }

    setAIEnhancementLoading(true);
    setSnackbarMessage(`Enhancing section with AI (${enhancementType})...`);
    setSnackbarOpen(true);
    
    try {
      const projectId = sessionStorage.getItem('projectId');
      
      const response = await enhanceTechnicalSectionWithAI(
        projectId,
        currentSection.title,
        currentSectionContent,
        enhancementType,
        ''
      );
      
      if (response.success) {
        const content = response.enhanced_content;
        
        if (enhancementType === 'suggest') {
          setAISuggestions([content]);
          setShowAISuggestions(true);
        } else {
          setCurrentSectionContent(currentSectionContent + "\n\n" + content);
        }
        
        setSnackbarMessage(`AI ${enhancementType} completed successfully for technical specification`);
      } else {
        setSnackbarMessage(response.error || `Error with AI ${enhancementType}`);
      }
    } catch (error) {
      console.error('AI enhancement error:', error);
      setSnackbarMessage(`Error: ${error.message}`);
    } finally {
      setAIEnhancementLoading(false);
      setAIMenuAnchor(null);
    }
  };

  const handleSaveDocument = async () => {
    setSavingDocument(true);
    setConfirmSaveOpen(false);
    
    try {
      if (!currentUser) {
        setSnackbarMessage('You must be logged in to save documents');
        setSnackbarOpen(true);
        setSavingDocument(false);
        return;
      }
      
      const projectId = sessionStorage.getItem('projectId');
      
      const companyInfo = {
        name: formData.project_name || 'SAP Technical Specification',
        author: formData.author_name || currentUser.full_name,
        date: formData.date || new Date().toISOString().split('T')[0],
        client: formData.client_name || formData.project_name,
        project_id: projectId
      };
      
      const metadata = {
        ...companyInfo,
        document_name: documentName,
        generated_by: 'ai',
        ai_model: 'gpt-4o',
        generation_timestamp: new Date().toISOString(),
        form_data: formData
      };
      
      const existingDocumentId = savedDocumentId || sessionStorage.getItem('saved_document_id');
      const isNewDocument = sessionStorage.getItem('isNewDocument') === 'true';
      
      console.log(`Saving technical specification. Existing ID: ${existingDocumentId}, Is new document: ${isNewDocument}`);
      
      const markdownResult = await saveMarkdown(
        document, 
        documentName, 
        metadata, 
        isNewDocument ? null : existingDocumentId,
        isNewDocument,
        projectId,
        'technical_spec' // Specify technical specification document type
      );
      
      if (!markdownResult.success) {
        throw new Error(markdownResult.error || 'Failed to save document');
      }
      
      const documentId = markdownResult.document_id;
      console.log(`Technical specification saved with ID: ${documentId}`);
      
      // Update document content with correct document ID if needed
      if (documentId && document.includes('| Document ID |')) {
        const docInfoPattern = /(\| Document ID \| )([A-Za-z0-9-]+)( \|)/;
        const updatedDocument = document.replace(docInfoPattern, `$1${documentId}$3`);
        if (updatedDocument !== document) {
          console.log(`Updated document content with correct document ID: ${documentId}`);
          setDocument(updatedDocument);
          sessionStorage.setItem('generatedDocument', updatedDocument);
        }
      }
      
      if (documentId) {
        sessionStorage.setItem('saved_document_id', documentId);
        sessionStorage.setItem('isNewDocument', 'false');
        setSavedDocumentId(documentId);
        
        // Create PDF and DOCX versions
        try {
          await createDocument(document, 'pdf', companyInfo, documentId);
          await createDocument(document, 'docx', companyInfo, documentId);
          console.log('Created PDF and DOCX versions successfully');
        } catch (formatError) {
          console.warn('Error creating additional formats:', formatError);
        }
      }
      
      setDocumentSaved(true);
      sessionStorage.setItem('documentSaved', 'true');
      setSnackbarMessage(`Technical specification saved successfully! Document ID: ${documentId}`);
      setSnackbarOpen(true);
      
    } catch (error) {
      console.error('Error saving document:', error);
      setSnackbarMessage(`Error saving document: ${error.message}`);
      setSnackbarOpen(true);
    } finally {
      setSavingDocument(false);
    }
  };

  const handleDownload = async (format) => {
    if (savedDocumentId) {
      const success = await downloadDocumentById(savedDocumentId, null, format);
      if (!success) {
        setSnackbarMessage(`Failed to download ${format.toUpperCase()} file`);
        setSnackbarOpen(true);
      }
    } else {
      const projectId = sessionStorage.getItem('projectId');
      const companyInfo = {
        name: formData.project_name || 'SAP Technical Specification',
        author: formData.author_name || currentUser?.full_name || 'Generated',
        date: formData.date || new Date().toISOString().split('T')[0],
        client: formData.client_name || formData.project_name,
        project_id: projectId
      };
      
      const success = await downloadDocument(document, format, companyInfo);
      if (!success) {
        setSnackbarMessage(`Failed to download ${format.toUpperCase()} file`);
        setSnackbarOpen(true);
      }
    }
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
          <Typography variant="h6" sx={{ ml: 2 }}>Loading technical specification...</Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" color="error" gutterBottom>
            Error
          </Typography>
          <Typography>{error}</Typography>
          <Button 
            variant="contained" 
            onClick={() => navigate('/technical-specifications')} 
            sx={{ mt: 2 }}
          >
            Generate New Technical Specification
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{
              background: 'linear-gradient(to right, #F5821F, #F5821F88)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            SAP Technical Specification
          </Typography>
          <Tooltip title="Edit document inputs">
            <IconButton 
              color="primary" 
              onClick={() => setEditDialogOpen(true)}
              disabled={!documentSaved}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        </Box>
        
        {/* Document Status */}
        <Box display="flex" alignItems="center" gap={2} mt={2}>
          <Chip 
            label={documentSaved ? "Saved" : "Not Saved"} 
            color={documentSaved ? "success" : "warning"}
            size="small"
          />
          {savedDocumentId && (
            <Chip 
              label={`ID: ${savedDocumentId}`} 
              variant="outlined"
              size="small"
            />
          )}
        </Box>
        
        {/* Action Buttons */}
        <Box display="flex" gap={2} mt={3}>
          <Button
            variant="contained"
            startIcon={savingDocument ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={() => setConfirmSaveOpen(true)}
            disabled={savingDocument}
            sx={{ 
              backgroundColor: documentSaved ? theme.palette.success.main : theme.palette.primary.main,
              '&:hover': {
                backgroundColor: documentSaved ? theme.palette.success.dark : theme.palette.primary.dark,
              }
            }}
          >
            {savingDocument ? 'Saving...' : (documentSaved ? 'Update Document' : 'Save Document')}
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={() => handleDownload('pdf')}
          >
            Download PDF
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<DescriptionIcon />}
            onClick={() => handleDownload('docx')}
          >
            Download DOCX
          </Button>
        </Box>
      </Paper>

      {/* Document Tags and Access */}
      {savedDocumentId && (
        <Box mb={3}>
          <DocumentTags 
            documentId={savedDocumentId} 
            tags={tags} 
            onTagsChange={setTags}
          />
          <DocumentProjectAccess documentId={savedDocumentId} />
        </Box>
      )}

      {/* View Mode Toggle */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={viewMode === 'edit'}
              onChange={(e) => setViewMode(e.target.checked ? 'edit' : 'preview')}
            />
          }
          label="Edit Mode"
        />
      </Paper>

      {/* Document Content */}
      <Paper sx={{ p: 3 }}>
        {viewMode === 'preview' ? (
          <Box>
            <ReactMarkdown>{document}</ReactMarkdown>
            
            {/* Section Edit Buttons */}
            <Box mt={4}>
              <Typography variant="h6" gutterBottom>Edit Sections</Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {documentSections.map((section, index) => (
                  <Button
                    key={index}
                    variant="outlined"
                    size="small"
                    onClick={() => handleEditSection(section)}
                    startIcon={<EditIcon />}
                  >
                    {section.title}
                  </Button>
                ))}
              </Box>
            </Box>
          </Box>
        ) : (
          <TextField
            multiline
            fullWidth
            rows={30}
            value={document}
            onChange={(e) => {
              setDocument(e.target.value);
              sessionStorage.setItem('generatedDocument', e.target.value);
            }}
            variant="outlined"
            sx={{ fontFamily: 'monospace' }}
          />
        )}
      </Paper>

      {/* Section Edit Dialog */}
      <Dialog 
        open={sectionEditDialogOpen} 
        onClose={() => setSectionEditDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            Edit Section: {currentSection?.title}
            <Box>
              <IconButton
                onClick={(e) => setAIMenuAnchor(e.currentTarget)}
                disabled={aiEnhancementLoading}
                color="primary"
              >
                {aiEnhancementLoading ? <CircularProgress size={24} /> : <AutoAwesomeIcon />}
              </IconButton>
              <IconButton onClick={() => setSectionEditDialogOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            multiline
            fullWidth
            rows={15}
            value={currentSectionContent}
            onChange={(e) => setCurrentSectionContent(e.target.value)}
            variant="outlined"
            sx={{ mt: 1, fontFamily: 'monospace' }}
          />
          
          {/* AI Suggestions */}
          {showAISuggestions && aiSuggestions.length > 0 && (
            <Box mt={2}>
              <Typography variant="h6">AI Suggestions:</Typography>
              {aiSuggestions.map((suggestion, index) => (
                <Paper key={index} sx={{ p: 2, mt: 1, backgroundColor: '#f5f5f5' }}>
                  <ReactMarkdown>{suggestion}</ReactMarkdown>
                  <Button
                    size="small"
                    onClick={() => {
                      setCurrentSectionContent(currentSectionContent + "\n\n" + suggestion);
                      setShowAISuggestions(false);
                      setAISuggestions([]);
                    }}
                  >
                    Apply This Suggestion
                  </Button>
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSectionEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveSection} variant="contained">Save Section</Button>
        </DialogActions>
      </Dialog>

      {/* AI Enhancement Menu */}
      <Menu
        anchorEl={aiMenuAnchor}
        open={Boolean(aiMenuAnchor)}
        onClose={() => setAIMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleAIEnhancement('expand')}>
          <ListItemIcon><TipsAndUpdatesIcon color="primary" /></ListItemIcon>
          <ListItemText primary="Expand with Technical Details" />
        </MenuItem>
        
        <MenuItem onClick={() => handleAIEnhancement('refine')}>
          <ListItemIcon><AutoFixHighIcon color="primary" /></ListItemIcon>
          <ListItemText primary="Refine Technical Content" />
        </MenuItem>
        
        <MenuItem onClick={() => handleAIEnhancement('technical_focus')}>
          <ListItemIcon><CodeIcon color="primary" /></ListItemIcon>
          <ListItemText primary="Add Technical Focus" />
        </MenuItem>
        
        <MenuItem onClick={() => handleAIEnhancement('enterprise_ready')}>
          <ListItemIcon><BusinessIcon color="primary" /></ListItemIcon>
          <ListItemText primary="Make Enterprise Ready" />
        </MenuItem>
        
        <Divider />
        
        <MenuItem onClick={() => handleAIEnhancement('suggest')}>
          <ListItemIcon><AccountTreeIcon color="primary" /></ListItemIcon>
          <ListItemText primary="Get AI Suggestions" />
        </MenuItem>
      </Menu>

      {/* Save Confirmation Dialog */}
      <Dialog open={confirmSaveOpen} onClose={() => setConfirmSaveOpen(false)}>
        <DialogTitle>Save Technical Specification</DialogTitle>
        <DialogContent>
          <Typography>
            {documentSaved 
              ? 'This will update your existing technical specification with the current changes.'
              : 'This will save your technical specification to the database and create downloadable PDF/DOCX versions.'
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSaveOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveDocument} variant="contained">
            {documentSaved ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="info">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ViewTechnicalSpecPage;